import { CreatePartnerRequest, useCreatePartner } from 'services/repositories/partners/partners';
import { CreateTradeRequestRequest, useCreateTradeRequest } from 'services/repositories/relationships/relationships';
import { DocumentType, PartnerDocumentTypes, PartnerDTO } from 'support/types';

export type DocumentTypeStatus = 'available' | 'notAvailable' | 'connected';

export type DocumentTypeSetting = {
  documentType: DocumentType;
  outgoing: { currentStatus: DocumentTypeStatus; selected: boolean };
  incoming: { currentStatus: DocumentTypeStatus; selected: boolean };
};

export const defaultDocumentSettings: Array<DocumentTypeSetting> = [
  {
    documentType: DocumentType.order,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.orderResponse,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.shippingNotice,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.invoice,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.creditNote,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.dispatchInstruction,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.dispatchResponse,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.productCatalog,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.inventoryReport,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.receivalNotice,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.remittanceAdvice,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
  {
    documentType: DocumentType.salesReport,
    incoming: { currentStatus: 'notAvailable', selected: false },
    outgoing: { currentStatus: 'notAvailable', selected: false },
  },
];

export const mapPartnerDocumentTypes = ({
  partnerDocuments,
  isCurrentPartner,
}: {
  partnerDocuments?: PartnerDocumentTypes;
  isCurrentPartner?: boolean;
}): Array<DocumentTypeSetting> => {
  const mappedDocumentTypes: Array<DocumentTypeSetting> = [];

  defaultDocumentSettings.forEach((defaultDocumentSetting) => {
    const incoming = partnerDocuments?.sends.find(
      (partnerDocument) => partnerDocument.type === defaultDocumentSetting.documentType,
    );

    const outgoing = partnerDocuments?.receives.find(
      (partnerDocument) => partnerDocument.type === defaultDocumentSetting.documentType,
    );

    const mappedDocument: DocumentTypeSetting = {
      documentType: defaultDocumentSetting.documentType,
      incoming: isCurrentPartner
        ? {
            currentStatus: outgoing ? (outgoing.connected ? 'connected' : 'available') : 'notAvailable',
            selected: false,
          }
        : {
            currentStatus: incoming ? (incoming.connected ? 'connected' : 'available') : 'notAvailable',
            selected: false,
          },
      outgoing: isCurrentPartner
        ? {
            currentStatus: incoming ? (incoming.connected ? 'connected' : 'available') : 'notAvailable',
            selected: false,
          }
        : {
            currentStatus: outgoing ? (outgoing.connected ? 'connected' : 'available') : 'notAvailable',
            selected: false,
          },
    };

    mappedDocumentTypes.push(mappedDocument);
  });

  return mappedDocumentTypes;
};

export const createTradePartnerRequests = async ({
  createPartnerMutation,
  createTradeRequestMutation,
  currentPartner,
  submission: { tradePartners, requestedDocumentTypes, additionalInformation, message, includeAsCCRecipient },
}: {
  createPartnerMutation: ReturnType<typeof useCreatePartner>;
  createTradeRequestMutation: ReturnType<typeof useCreateTradeRequest>;
  currentPartner: PartnerDTO;
  submission: {
    message?: string;
    additionalInformation?: string;
    tradePartners: Required<CreatePartnerRequest>['partners'] &
      Array<{
        partyIdentifierDomain: Required<CreateTradeRequestRequest>['tradeRequests'][number]['partyIdentifierDomain'];
        partyIdentifier: Required<CreateTradeRequestRequest>['tradeRequests'][number]['partyIdentifier'];
      }>;
    requestedDocumentTypes: Array<DocumentTypeSetting>;
    includeAsCCRecipient?: boolean;
  };
}): Promise<Array<string>> => {
  const newTradePartners = await createPartnerMutation.mutateAsync({
    partners: tradePartners.map((tradePartner) => ({
      name: tradePartner.name,
      contactPersons: [
        {
          language: tradePartner.contactPersons[0].language,
          name: tradePartner.contactPersons[0].name,
          email: tradePartner.contactPersons[0].email,
          phone: tradePartner.contactPersons[0].phone,
        },
      ],
    })),
  });

  const tradeRequests = newTradePartners.map((newTradePartner, tradePartnerIndex) => {
    const relationships = createDocumentTypesPayload(
      currentPartner.id,
      newTradePartner.partnerId,
      requestedDocumentTypes,
    );

    return {
      partyIdentifierDomain: tradePartners[tradePartnerIndex].partyIdentifierDomain,
      partyIdentifier: tradePartners[tradePartnerIndex].partyIdentifier,
      partnerId: newTradePartner.partnerId,
      additionalInformation,
      relationships,
      message,
      includeAsCCRecipient,
    };
  });

  await createTradeRequestMutation.mutateAsync({ tradeRequests });

  return newTradePartners.map((p) => p.partnerId);
};

export const createDocumentTypesPayload = (
  currentPartner: PartnerDTO['id'],
  tradePartner: PartnerDTO['id'],
  documentTypesSettings: Array<DocumentTypeSetting>,
) => {
  const relationships = documentTypesSettings.reduce<
    Required<CreateTradeRequestRequest>['tradeRequests'][number]['relationships']
  >((carry, setting) => {
    const newRelationships = [];

    if (setting.outgoing.selected) {
      newRelationships.push({
        documentType: setting.documentType,
        sender: { partnerId: currentPartner },
        receiver: { partnerId: tradePartner },
      });
    }

    if (setting.incoming.selected) {
      newRelationships.push({
        documentType: setting.documentType,
        sender: { partnerId: tradePartner },
        receiver: { partnerId: currentPartner },
      });
    }

    return [...carry, ...newRelationships];
  }, []);

  return relationships;
};
